import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Copy as CopyIcon, Loader2, Lock, Sparkles, Wand2 } from "lucide-react";
import { api, formatError } from "../lib/api";

const PLATFORM_LABELS = {
  facebook: "Facebook", instagram: "Instagram", twitter: "X (Twitter)",
  tiktok: "TikTok", linkedin: "LinkedIn", youtube_short: "YouTube Shorts",
  reddit: "Reddit", pinterest: "Pinterest",
};

export default function PublicBusinessAdsPage() {
  const nav = useNavigate();
  const [offer, setOffer] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [website, setWebsite] = useState("");
  const [platform, setPlatform] = useState("twitter");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (offer.trim().length < 10) {
      toast.error("Describe your offer in a sentence or two.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/public/business-ad", {
        offer: offer.trim(),
        platform,
        business_name: businessName.trim(),
        website: website.trim(),
      });
      setResult(data);
      toast.success("Sample ad generated!");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Generation failed";
      toast.error(typeof detail === "string" ? detail : formatError(detail), { duration: 7000 });
    } finally {
      setGenerating(false);
    }
  };

  const copyAd = async () => {
    if (!result?.ad) return;
    const text = (result.ad.copy || "") + (result.ad.hashtags?.length ? "\n\n" + result.ad.hashtags.join(" ") : "");
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  };

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50" data-testid="public-business-ads-page">
      {/* Top nav */}
      <header className="border-b border-zinc-200 dark:border-zinc-800 sticky top-0 bg-white/95 dark:bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-black text-lg">
            <span className="text-brand">⚡</span> CopyForge
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/login" className="hover:underline">Login</Link>
            <Link to="/register?intent=business" className="bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-3 py-1.5 font-bold hover:bg-zinc-700 dark:hover:bg-zinc-300">
              Sign up free
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-zinc-100 dark:bg-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3" /> Free sample · No signup
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-3">
            Generate a business ad in 10 seconds.
          </h1>
          <p className="text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
            See what CopyForge writes for your business — free for one platform, no credit card. Unlock all 8 social platforms when you sign up.
          </p>
        </div>

        {!result && (
          <section className="border-2 border-zinc-900 dark:border-zinc-100 bg-white dark:bg-zinc-950 p-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500">What are you advertising? <span className="text-red-500">*</span></span>
              <textarea
                value={offer}
                onChange={(e) => setOffer(e.target.value)}
                rows={4}
                placeholder="E.g. Launch our Spring 2026 First-Flush Darjeeling tea. 15% off with code BLOOM15."
                data-testid="public-offer-input"
                className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Business name</span>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="AcmeTeas"
                  data-testid="public-business-name"
                  className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Website</span>
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="acmeteas.com"
                  data-testid="public-website"
                  className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500">Platform (free preview)</span>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                data-testid="public-platform-select"
                className="mt-1 w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-sm"
              >
                {Object.entries(PLATFORM_LABELS).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={generate}
              disabled={generating || offer.trim().length < 10}
              data-testid="public-generate-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 font-bold disabled:opacity-50"
            >
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Wand2 className="w-4 h-4" /> Generate my free ad</>}
            </button>
            <p className="text-xs text-zinc-500 text-center">No signup required · 1 free ad per day</p>
          </section>
        )}

        {result && (
          <section className="space-y-6">
            <article className="border-2 border-green-600 dark:border-green-500 bg-green-50/30 dark:bg-green-950/20 p-6">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-green-700 dark:text-green-400" />
                <h2 className="font-bold text-lg">{PLATFORM_LABELS[result.ad.platform]} — Free Sample</h2>
                <span className="ml-auto text-xs text-zinc-500">{result.ad.char_count}/{result.ad.char_limit}</span>
              </div>
              <div className="text-sm whitespace-pre-wrap leading-relaxed mb-3" data-testid="public-result-copy">
                {result.ad.copy}
              </div>
              {result.ad.hashtags?.length > 0 && (
                <div className="text-xs text-zinc-500 break-words mb-3">{result.ad.hashtags.join("  ")}</div>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={copyAd} className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 dark:border-zinc-700 text-xs font-bold hover:bg-white dark:hover:bg-zinc-900">
                  <CopyIcon className="w-3.5 h-3.5" /> Copy
                </button>
                {result.ad.share_url && (
                  <a href={result.ad.share_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 text-xs font-bold">
                    Share now
                  </a>
                )}
              </div>
            </article>

            <div className="border-2 border-zinc-900 dark:border-zinc-100 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 p-6 md:p-8 text-center">
              <Lock className="w-8 h-8 mx-auto mb-3" />
              <h3 className="text-2xl md:text-3xl font-black mb-2">{result.upsell.headline}</h3>
              <p className="text-sm opacity-80 mb-5">Unlock all 8 platforms + Brand Kit + image generation.</p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-5">
                {result.locked_platforms.map((p) => (
                  <div key={p.platform} className="flex items-center gap-1 px-3 py-1.5 border border-current/30 text-xs opacity-80" data-testid={`locked-${p.platform}`}>
                    <Lock className="w-3 h-3" /> {p.platform_name}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button type="button" onClick={() => nav("/register?intent=business")} data-testid="public-upgrade-monthly" className="bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white px-6 py-3 font-bold hover:opacity-90">
                  Start with $149/mo Business
                </button>
                <button type="button" onClick={() => nav("/founders")} data-testid="public-upgrade-lifetime" className="border-2 border-white dark:border-zinc-900 px-6 py-3 font-bold hover:bg-white/10 dark:hover:bg-zinc-900/10">
                  Or $497 lifetime →
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={() => { setResult(null); setOffer(""); }}
              className="text-sm underline text-zinc-500 mx-auto block"
              data-testid="public-try-again"
            >
              Try another offer (resets tomorrow)
            </button>
          </section>
        )}
      </main>
    </div>
  );
}
